import React from 'react'
import { MapView } from '@fareye/ui'
import {
  isEmpty,
  FaCheckCircle,
  FaMapMarkerAlt,
  FaRegDotCircle
} from '@fareye/utils'
import { useLocation, ILocationPayload } from '@fareye/common'
import { L, latLng, Polyline } from '@fareye/ui'
import { renderToStaticMarkup } from 'react-dom/server'
import { HistoryMarker } from '../../../components/Marker/HistoryMarker'
import { StoppageMarker } from '../../../components/Marker/StoppageMarker'
import { ExceptionMarker } from '../../../components/Marker/ExceptionMarker'
import { ConsignerMarker } from './ConsignerMarker'
import { ConsigneeMarker } from './ConsigneeMarker'
import { StopsMarker } from './StopsMarker'
import { CurrentLocationMarker } from './CurrentLocationMarker'
import { Plane, Rail, Ship, Truck } from '../../../style/styled'
import { TripPoiMarker } from '../../../components/Marker/TripPoiMarker'
import { LatestVehiclePingMarker } from '../../../components/Marker/LatestVehiclePingMarker'

const initCenter = latLng(0, 0)
export const MapDisplay = React.memo(
  ({
    mapKey,
    defaultBaseLayer,
    data,
    otherData,
    selectedId,
    histories,
    stoppages,
    moveMarker,
    aerisLayer,
    trackingProvider
  }: any) => {
    let { exceptions = [] } = otherData
    let {
      history = [],
      journeyPois = [],
      consigneeLat,
      consigneeLong,
      consignerLong,
      consignerLat,
      latestJourney,
      latestVehiclePing = {}
    } = data
    const zoom = 14
    const [center, setCenter] = React.useState(initCenter)
    let stopsData: any[] = data.stops
      ? data.stops
      : data.nodes
      ? data.nodes
      : []
    const originData = stopsData[0] || {}
    const validConsigneeCoords = !!consigneeLat && !!consigneeLong
    const validConsignerCoords = !!consignerLat && !!consignerLong
    const validLatLong = validConsigneeCoords || validConsignerCoords
    const destinationData = stopsData[stopsData.length - 1] || {}
    let bounds = L.latLngBounds(
      L.latLng(28.7041, 77.1025),
      L.latLng(13.0827, 80.2707)
    )
    //added hack for antimeridian update
    let latlngs: any = histories.map((hist: any) => {
      return [hist.latitude, hist.longitude]
    })
    let identity = false,
      narrow = false

    if (latlngs) {
      let posCount = 0
      let maxLng = null
      let minLng = null
      for (const latlng of latlngs) {
        let lng = latlng[1]
        if (!maxLng || maxLng < lng) {
          maxLng = lng
        }
        if (!minLng || minLng > lng) {
          minLng = lng
        }
        if (lng >= 0) {
          posCount++
        }
      }

      const allSameSign = posCount == 0 || posCount === latlngs.length
      if (allSameSign) {
        identity = true
      } else {
        narrow = maxLng - minLng < 180
      }
    } else {
      identity = true
    }

    function convert(latlng: [number, number]) {
      if (identity) {
        return latlng[1]
      }

      if (narrow) {
        return latlng[1]
      } else {
        let lng = latlng[1]
        if (lng < 0) {
          return lng + 360
        } else {
          return latlng[1]
        }
      }
    }

    histories = histories.map((hist: any) => {
      return { ...hist, longitude: convert([hist.latitude, hist.longitude]) }
    })
    history = history.map((hist: any) => {
      return { ...hist, longitude: convert([hist.latitude, hist.longitude]) }
    })

    consigneeLong = convert([consigneeLat, consigneeLong])
    consignerLong = convert([consignerLat, consignerLong])

    data = { ...data, consigneeLong, consignerLong }
    stoppages = stoppages.map((hist: any) => {
      return { ...hist, startLong: convert([hist.startLat, hist.startLong]) }
    })
    exceptions = exceptions.map((hist: any) => {
      return {
        ...hist,
        coords: {
          lat: hist.coords.lat,
          lng: convert([hist.coords.lat, hist.coords.lng])
        }
      }
    })
    stopsData = stopsData.map((hist: any) => {
      return { ...hist, longitude: convert([hist.latitude, hist.longitude]) }
    })
    journeyPois = journeyPois.map((hist: any) => {
      return { ...hist, longitude: convert([hist.latitude, hist.longitude]) }
    })

    if (
      !isEmpty(history) ||
      validConsigneeCoords ||
      validConsignerCoords ||
      !isEmpty(stopsData)
    ) {
      bounds = L.latLngBounds([
        ...history.map((location: any) =>
          L.latLng(Number(location.latitude), Number(location.longitude))
        ),
        ...(validConsigneeCoords
          ? [L.latLng(Number(consigneeLat), Number(consigneeLong))]
          : []),
        ...(validConsignerCoords
          ? [L.latLng(Number(consignerLat), Number(consignerLong))]
          : []),
        ...stopsData.map((location: any) =>
          L.latLng(Number(location.latitude), Number(location.longitude))
        )
      ])
    }

    React.useEffect(() => {
      if (
        validLatLong &&
        consigneeLat === consignerLat &&
        consigneeLong === consignerLong
      ) {
        setCenter(latLng(consigneeLat, consigneeLong))
      } else if (!bounds) {
        useLocation(({ data, success }: ILocationPayload) => {
          if (success) {
            setCenter(latLng(data.coords.latitude, data.coords.longitude))
          }
        })
      }
    }, [])

    const transportMode = latestJourney
      ? latestJourney.transportMode
      : data.transportMode

    let tptMode: any = undefined
    if (transportMode === 'air') {
      tptMode = Plane
    } else if (transportMode === 'ocean') {
      tptMode = Ship
    } else if (transportMode === 'road') {
      tptMode = Truck
    } else if (transportMode === 'rail') {
      tptMode = Rail
    } else {
      tptMode = Truck
    }
    const iconMarkup = (Component: any, color = '#ffffff') =>
      renderToStaticMarkup(
        <Component color={color} size={Component !== FaCheckCircle ? 25 : 20} />
      )

    const customMarkerIcon = (Component: any, color?: string) =>
      L.divIcon({
        html: iconMarkup(Component, color),
        className: 'custom-marker',
        iconAnchor: [12.5, 12.5],
        iconSize: [25, 25]
      })

    let showLatestVehiclePingMarker = false
    if (!isEmpty(latestVehiclePing)) {
      const lastHistory = !isEmpty(histories)
        ? histories[histories.length - 1]
        : null
      showLatestVehiclePingMarker = !(
        lastHistory &&
        lastHistory.gpsTimestamp === latestVehiclePing.gpsTimestamp
      )
      showLatestVehiclePingMarker =
        showLatestVehiclePingMarker &&
        latestVehiclePing.gpsTimestamp >= data.actStartTime?.time
    }
    return (
      <MapView
        leafletMapConfig={{
          ...(!bounds ? { center: center } : {}),
          zoom,
          bounds
        }}
        style={{
          height: '100%',
          width: '100%'
        }}
        mapKey={mapKey}
        aerisLayer={aerisLayer}
        defaultBaseLayer={defaultBaseLayer}
      >
        {stopsData.map((stop: any, i: number) => {
          const atStopFlag = stop?.ata && !stop?.atd
          return (
            i > 0 &&
            i !== stopsData.length - 1 && (
              <StopsMarker
                icon={customMarkerIcon(
                  atStopFlag ? FaRegDotCircle : FaCheckCircle,
                  stop.state === 'completed' || atStopFlag ? 'green' : '#808080'
                )}
                indexNumber={i}
                data={stop}
                apiData={data}
                key={`stops_${stop.name}_${stop.latitude}_${stop.longitude}`}
              />
            )
          )
        })}
        <CurrentLocationMarker
          data={data}
          lastLocation={
            !isEmpty(histories) ? histories[histories.length - 1] : {}
          }
          icon={customMarkerIcon(tptMode, '')}
          moveMarker={moveMarker}
          trackingProvider={trackingProvider}
        />
        {consigneeLat && consigneeLong ? (
          <ConsigneeMarker
            data={data}
            stopData={destinationData}
            icon={customMarkerIcon(FaMapMarkerAlt, 'red')}
            // selected={}
          />
        ) : null}

        {consignerLat && consignerLong ? (
          <ConsignerMarker
            data={data}
            stopData={originData}
            icon={customMarkerIcon(FaMapMarkerAlt, 'green')}
            // selected={}
          />
        ) : null}
        {exceptions &&
          exceptions.map((exception: any, index: number) => (
            <ExceptionMarker
              exception={exception}
              selected={selectedId.exception === exception.id}
              type={'UTC'}
              key={`exceptionMarker_${index}`}
            />
          ))}
        {stoppages &&
          stoppages.map((stoppage: any, index: number) => (
            <StoppageMarker
              iconUrl="https://i.imgur.com/ZmdktGt.png"
              iconSize={[20, 20]}
              key={`stoppageMarker_${index}`}
              type={'UTC'}
              stoppage={stoppage}
              selected={selectedId.stoppage === stoppage.id}
            />
          ))}

        {!isEmpty(histories) && (
          <>
            {histories.map((history: any, index: number) => (
              <HistoryMarker
                iconUrl="https://i.imgur.com/WK4MLHw.png"
                iconSize={[5, 5]}
                key={`historyMarker_${history.latitude}_${history.longitude}_${history.gpsTimestamp}_${index}`}
                history={history}
                type={'UTC'}
                selected={
                  selectedId.location ===
                  `${history.vehicleNo}${history.gpsTimestamp}`
                }
              />
            ))}
            <Polyline
              positions={histories.map((history: any, index: number) =>
                L.latLng(history.latitude, history.longitude)
              )}
            />
          </>
        )}
        {showLatestVehiclePingMarker && !isEmpty(latestVehiclePing) ? (
          <LatestVehiclePingMarker data={latestVehiclePing} type={'UTC'} />
        ) : null}
        {journeyPois?.length ? (
          <>
            {journeyPois.map((poi: any) => (
              <TripPoiMarker
                {...poi}
                key={`poi_${poi.poiNickName}_${poi.poiType}_${poi.radius}`}
              />
            ))}
          </>
        ) : null}
      </MapView>
    )
  }
)
